/**
 * SoleadLab 2.0 - Thermal Annotator
 *
 * Canvas overlay on a thermal image for drawing ROI rectangles
 * and point measurements. The canvas sits absolutely over the
 * <img> element and intercepts pointer events.
 *
 * REFERENCE: editor (23).html .img-annot-wrap + _drawOverlays (lines 7547-7586)
 */

import { useRef, useEffect, useCallback } from 'react';
import type { ThermalRoi, ThermalPoint } from './thermalTypes';
import './ThermalAnnotator.css';

interface ThermalAnnotatorProps {
  imageSrc: string;
  rois: ThermalRoi[];
  points: ThermalPoint[];
  activeRoiIndex: number;
  onPointerDown: (imgX: number, imgY: number) => void;
  onPointerMove: (imgX: number, imgY: number) => void;
  onPointerUp: () => void;
  imgRef: React.RefObject<HTMLImageElement | null>;
  /** When false, canvas renders overlays but ignores pointer events */
  interactive?: boolean;
}

export default function ThermalAnnotator({
  imageSrc,
  rois,
  points,
  activeRoiIndex,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  imgRef,
  interactive = true,
}: ThermalAnnotatorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  // ---- Coordinate conversion: canvas client → image pixel ----
  const clientToImgCoords = useCallback((clientX: number, clientY: number): [number, number] => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !img.naturalWidth) return [0, 0];

    const rect = canvas.getBoundingClientRect();
    const canvasX = clientX - rect.left;
    const canvasY = clientY - rect.top;

    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;

    return [canvasX * scaleX, canvasY * scaleY];
  }, [imgRef]);

  // ---- Canvas drawing ----
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !img.naturalWidth) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Match canvas resolution to display size
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const cw = Math.round(rect.width * dpr);
    const ch = Math.round(rect.height * dpr);
    if (canvas.width !== cw || canvas.height !== ch) {
      canvas.width = cw;
      canvas.height = ch;
    }

    ctx.clearRect(0, 0, cw, ch);
    ctx.scale(dpr, dpr);

    // Scale factors: image pixels → CSS pixels
    const sx = rect.width / img.naturalWidth;
    const sy = rect.height / img.naturalHeight;

    // ---- Draw ROI rectangles ----
    ctx.save();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#1d4ed8';
    ctx.fillStyle = 'rgba(29, 78, 216, 0.12)';
    ctx.font = '12px Inter, Arial, sans-serif';

    for (let i = 0; i < rois.length; i++) {
      const r = rois[i];
      const rx0 = Math.min(r.x0, r.x1) * sx;
      const ry0 = Math.min(r.y0, r.y1) * sy;
      const rw = Math.abs(r.x1 - r.x0) * sx;
      const rh = Math.abs(r.y1 - r.y0) * sy;

      ctx.beginPath();
      ctx.rect(rx0, ry0, rw, rh);
      ctx.stroke();
      ctx.fill();

      // Label
      const label = r.stats && typeof r.stats.max_c === 'number'
        ? `ROI${i + 1} max ${r.stats.max_c.toFixed(2)}°C`
        : `ROI${i + 1}`;
      ctx.fillStyle = '#111';
      ctx.fillText(label, rx0 + 6, ry0 + 14);
      ctx.fillStyle = 'rgba(29, 78, 216, 0.12)';

      // Crosshair at hottest pixel
      if (r.stats && typeof r.stats.max_x === 'number') {
        const hx = r.stats.max_x * sx;
        const hy = r.stats.max_y * sy;
        const hs = 8;
        // White outline
        ctx.lineWidth = 3;
        ctx.strokeStyle = '#ffffff';
        ctx.beginPath();
        ctx.moveTo(hx - hs, hy); ctx.lineTo(hx + hs, hy);
        ctx.moveTo(hx, hy - hs); ctx.lineTo(hx, hy + hs);
        ctx.stroke();
        // Red cross
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = '#ff0000';
        ctx.beginPath();
        ctx.moveTo(hx - hs, hy); ctx.lineTo(hx + hs, hy);
        ctx.moveTo(hx, hy - hs); ctx.lineTo(hx, hy + hs);
        ctx.stroke();
        // Reset for next ROI
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#1d4ed8';
      }

      // Corner handles for active ROI
      if (i === activeRoiIndex) {
        ctx.fillStyle = '#1d4ed8';
        const hs = 5;
        const corners = [
          [rx0, ry0], [rx0 + rw, ry0],
          [rx0, ry0 + rh], [rx0 + rw, ry0 + rh],
        ];
        for (const [cx, cy] of corners) {
          ctx.beginPath();
          ctx.arc(cx, cy, hs, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = 'rgba(29, 78, 216, 0.12)';
      }
    }
    ctx.restore();

    // ---- Draw point crosses ----
    ctx.save();
    ctx.font = '12px Inter, Arial, sans-serif';

    for (let i = 0; i < points.length; i++) {
      const p = points[i];
      const px = p.x * sx;
      const py = p.y * sy;
      const s = 12;

      // White outline for visibility on any background
      ctx.lineWidth = 4;
      ctx.strokeStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(px - s, py);
      ctx.lineTo(px + s, py);
      ctx.moveTo(px, py - s);
      ctx.lineTo(px, py + s);
      ctx.stroke();

      // Red crosshair on top
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#cc0000';
      ctx.beginPath();
      ctx.moveTo(px - s, py);
      ctx.lineTo(px + s, py);
      ctx.moveTo(px, py - s);
      ctx.lineTo(px, py + s);
      ctx.stroke();

      // Center dot
      ctx.fillStyle = '#cc0000';
      ctx.beginPath();
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fill();

      // Label with white background for readability
      const label = p.value_c == null
        ? `P${i + 1}`
        : `P${i + 1} ${p.value_c.toFixed(2)}°C`;
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.fillRect(px + 10, py - 18, tw + 6, 16);
      ctx.fillStyle = '#cc0000';
      ctx.fillText(label, px + 13, py - 6);
    }
    ctx.restore();

    // Reset scale for next frame
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }, [rois, points, activeRoiIndex, imgRef]);

  // ---- Redraw on state changes ----
  useEffect(() => {
    const raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [draw]);

  // ---- Resize observer to keep canvas matched to image ----
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(draw);
    });
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [draw]);

  // ---- Pointer event handlers ----
  const handleDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging.current = true;
    const [imgX, imgY] = clientToImgCoords(e.clientX, e.clientY);
    onPointerDown(imgX, imgY);

    // Capture pointer for drag outside canvas
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [clientToImgCoords, onPointerDown]);

  const handleMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const [imgX, imgY] = clientToImgCoords(e.clientX, e.clientY);
    onPointerMove(imgX, imgY);
  }, [clientToImgCoords, onPointerMove]);

  const handleUp = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    e.preventDefault();
    onPointerUp();
    (e.target as HTMLElement).releasePointerCapture(e.pointerId);
  }, [onPointerUp]);

  return (
    <div className="thermal-annotator" ref={wrapRef}>
      <img
        ref={imgRef as React.LegacyRef<HTMLImageElement>}
        src={imageSrc}
        alt="Thermal preview"
        className="thermal-annotator__img"
        draggable={false}
        onLoad={() => requestAnimationFrame(draw)}
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
      <canvas
        ref={canvasRef}
        className={`thermal-annotator__canvas${interactive ? '' : ' thermal-annotator__canvas--readonly'}`}
        onPointerDown={interactive ? handleDown : undefined}
        onPointerMove={interactive ? handleMove : undefined}
        onPointerUp={interactive ? handleUp : undefined}
        onPointerCancel={interactive ? handleUp : undefined}
      />
    </div>
  );
}
